// libraries
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectEmailSendingFromState,
  selectEmailSentFromState,
  selectErrorDetailMapFromState,
} from 'src/app/ResetPassword/redux/resetPassword.selectors'
import AppName from 'src/components/AppName/AppName'
import Auth0ForgotPassword from 'src/components/Auth0ForgotPassword/Auth0ForgotPassword'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ForgotPasswordForm from 'src/components/ForgotPasswordForm/ForgotPasswordForm'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgCodeVerification from 'src/components/Svgs/SvgCodeVerification'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { AuthTypes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { signinUrl } from 'src/navigation/urls'
import { createSetEmail, postAuthType } from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectEmailFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface ResetPasswordFormProps {
  title?: React.ReactElement
  confirmPassword?: boolean
  setTopRightImage?: (image: React.ReactElement) => void
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = (props) => {
  const dispatch = useBackgroundDispatch()

  const email = useSelector(selectEmailFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const emailSent = useSelector(selectEmailSentFromState)
  const emailSending = useSelector(selectEmailSendingFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const appName = useSelector(selectAppName)
  const authType = useSelector(selectAuthTypeFromState)

  const onEmailChange = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const [showUpdatePassword, setShowUpdatePassword] = useState(false)

  useEffect((): void => {
    if (emailSent && authType) {
      setShowUpdatePassword(true)
    }
  }, [emailSent])

  return (
    <>
      {!showUpdatePassword ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 40px;
            `}
          >
            {props.title || <Trns source={'Reset your password'} />}
          </span>
          <p
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 30px;
            `}
          >
            <Trns
              source={
                'Make sure the email address below is what you used to create a profile with ${appName}.'
              }
              expressions={{
                appName: <AppName />,
              }}
            />
          </p>
          <TextInputField
            design={TextInputFieldDesigns.New}
            value={email}
            placeholder={translateSingleString(
              locale,
              'Enter email connected to ${appName}',
              { appName: translateSingleString(locale, appName) },
            )}
            onChange={onEmailChange}
            type={'email'}
            autoComplete={'email'}
            errors={errorDetailMap?.data}
            fieldLabel={<Trns source="Email" />}
          />
          <Button
            type={ButtonTypes.submit}
            css={css`
              margin-top: 40px;
            `}
            design={ButtonDesigns.designCta}
            text={<Trns source={'Continue'} />}
            loadingText={<Trns source={'Sending'} />}
            onClick={(): void => {
              props.setTopRightImage &&
                props.setTopRightImage(<SvgCodeVerification />)
              dispatch(
                postAuthType(
                  {
                    data: email,
                  },
                  !emailSent,
                ),
              )
              setShowUpdatePassword(true)
            }}
            loading={emailSending}
            disabled={emailSending}
            big
          />
          <HoverableLink
            to={signinUrl()}
            css={css`
              margin-top: 30px;
              align-self: center;
            `}
            underline
            ariaLabelSource={'Back to Login'}
          >
            <Trns source={'Back to Login'} />
          </HoverableLink>
        </div>
      ) : authType === AuthTypes.cognito ? (
        <ForgotPasswordForm email={email} />
      ) : (
        <Auth0ForgotPassword />
      )}
    </>
  )
}

export default ResetPasswordForm
